/**
 * What every screen would otherwise work out again on every render.
 *
 * X8: derive once per fetch, not once per render. These run inside a query's
 * `select`, so the work happens when the data changes and not when a sheet
 * opens or a filter moves. Each one is keyed on the identity of the array it
 * was given, so the five mounted screens sharing a query share one answer
 * rather than computing five — `select` is per observer in TanStack Query,
 * and five observers on `['feed']` would otherwise mean five passes over
 * 2,400 rows.
 *
 * **Nothing here mutates its input.** The rows belong to the query cache now,
 * and a screen that edits them edits what every other screen is reading —
 * which is what `Venues.tsx` used to do to `venues` on its way to rendering
 * (`parent.hero_key = v.hero_key`), harmlessly while each screen owned its
 * own fetch and not harmlessly now.
 */
import type { FeedRow } from './types'
import { laneOf, type Lane } from './entries'
import type { ShowingLite } from '../components/CardBody'
import { dropTwins } from './twins'

/** A feed row with its lane already worked out. `laneOf` is a scan down
 *  `MATCH_ORDER` and the feed screen alone asked for it three times per row
 *  per render. */
export type FeedRowLane = FeedRow & { lane: Lane }

/**
 * The lane of a row, from the derivation where there is one.
 *
 * Call sites that hold a row straight out of `useFeed()` could read `e.lane`,
 * but `mergeFilmRuns` returns `FeedRow` and the merge is what a screen holds
 * by the time it is grouping — so this reads the derived value when it is
 * there and falls back to computing it.
 *
 * The fallback is never wrong for a merged card: `laneOf` matches on `type`
 * and `discipline` only, and `mergeFilmRuns` changes neither.
 */
export const laneFor = (e: FeedRow): Lane => (e as Partial<FeedRowLane>).lane ?? laneOf(e)

/** One answer per input array, for as long as that array is alive. A
 *  `WeakMap` because the key is the cached rows themselves: when the query
 *  drops them the derivation goes with them. */
const memo = <In extends object, Out>(fn: (input: In) => Out) => {
  const seen = new WeakMap<In, Out>()
  return (input: In): Out => {
    const hit = seen.get(input)
    if (hit !== undefined) return hit
    const out = fn(input)
    seen.set(input, out)
    return out
  }
}

/**
 * The feed, as every screen wants it.
 *
 * One thing, not three. `withBorrowedSummaries` was deleted in phase 7b (see
 * the comment at `CardBody.tsx:273`), and `mergeFilmRuns` cannot live here:
 * it is a feed-screen step that must run *after* that screen's dismissed,
 * language, already-happened and hidden-lane filters — merging first would
 * fold a dismissed week of a film into the card that stands for the rest —
 * and it needs the showings, which are a different query. Home, Venues and
 * Saved do not merge at all. It stays in `Feed.tsx`, inside a `useMemo`. *
 * **This is the client's one twin filter, and since cards WP3 it is a safety
 * net.** The `feed` view drops twins and `feed_page`'s `removed` names a row
 * that became one, so a fresh or up-to-date cache holds none. What this still
 * catches is a twin cached before WP3 (the view sent twins then, and WP3 does
 * not move a twin's `updated_at`, so `removed` never names it) — every screen
 * reads the feed through `useFeed()`, whose `select` is this, so that row
 * never reaches Today, Browse, Year, Lists, search, `newIn`, or any count or
 * badge. See `twins.ts`.
 */
export const deriveFeed = memo((rows: FeedRow[]): FeedRowLane[] =>
  dropTwins(rows).map(withLane))

/** One derived row per cached row, for as long as the cached row is alive.
 *
 *  A delta pull (`mergeFeedRows`) keeps every unchanged row object and
 *  replaces only the ones `feed_page` re-sent, so keying on the row rather
 *  than only on the array means a refetch that changed three rows hands back
 *  2,397 derived rows that are `===` the ones before. That is what keeps
 *  TanStack's structural sharing of the `select` result (`replaceEqualDeep`,
 *  run once per observer on every data change) a walk over three rows
 *  instead of a field-by-field comparison of all of them — 1.1 s per tab
 *  switch at CPU x4 while `useFeed`'s `select` was an inline arrow and so
 *  ran, and compared, on every render (a4-13). */
const laned = new WeakMap<FeedRow, FeedRowLane>()
function withLane(e: FeedRow): FeedRowLane {
  let out = laned.get(e)
  if (!out) { out = { ...e, lane: laneOf(e) }; laned.set(e, out) }
  return out
}

/** Showings by the entry that owns them, so a card looking up its own dates
 *  is a lookup rather than a scan of 2,700 rows. */
export const indexShowings = memo((rows: ShowingLite[]): Map<string, ShowingLite[]> => {
  const by = new Map<string, ShowingLite[]>()
  for (const s of rows) {
    if (!s.entry_id) continue
    const list = by.get(s.entry_id)
    if (list) list.push(s); else by.set(s.entry_id, [s])
  }
  return by
})

/** A venue row, as the screens need it. Read from `venues` directly rather
 *  than off the feed, because the feed only ever carries the venue an entry
 *  is filed at — which is the thing the Venues screen stopped trusting.
 *
 *  `street`/`postcode`/`country`/`lat`/`lng`/`site_url` joined WP 3.4a: the
 *  place page's Info tab needs the address, the coordinates for a Maps link
 *  and the website, and rule 3 of its brief is "nothing is fetched per
 *  venue" — this hook is one of the three that has to suffice, so it carries
 *  what Info reads rather than the page opening a fourth query. */
export interface VenueRow {
  id: string
  name: string
  district: string | null
  kind: string | null
  commercial: boolean | null
  parent_id: string | null
  /** For matching a newsletter lead's `place_guess` — see `leadVenue.ts`. */
  aliases: string[] | null
  hero_key: string | null
  hero_alt: string | null
  hero_credit: string | null
  hero_license: string | null
  hero_source_url: string | null
  street: string | null
  postcode: string | null
  country: string | null
  lat: number | null
  lng: number | null
  site_url: string | null
  /** `venues.closed_at` (20261002010000): set when the place no longer
   *  operates. Optional, not only nullable — a `['venues']` cache persisted
   *  before the column existed has no key at all, and `venueOpen.ts` is the
   *  one reader, with `?? null`. */
  closed_at?: string | null
}

/**
 * A building with no photograph of its own borrows the first one from a hall
 * under it.
 *
 * This is what the `feed` view does with `coalesce(v.hero_key, p.hero_key)`
 * and what the Venues screen has to do for itself, because only buildings are
 * drawn there. It used to be a loop that assigned onto the fetched rows
 * mid-render; now the fetched rows are shared cache and it returns new ones.
 *
 * The five hero fields move together, always. Taking a hall's `hero_key` and
 * leaving the building's own (absent) credit behind is how a photograph loses
 * its licence, and a CC image without its author is an infringing copy.
 */
export const inheritVenueHeroes = memo((venues: VenueRow[]) => {
  const byId = new Map(venues.map(v => [v.id, v]))

  // Which hall each parent should borrow from — the first one with a picture,
  // in the order the rows arrived, which is the order the old in-place loop
  // settled on too.
  const donor = new Map<string, VenueRow>()
  for (const v of venues) {
    if (!v.parent_id || !v.hero_key) continue
    const parent = byId.get(v.parent_id)
    if (!parent || parent.hero_key || donor.has(parent.id)) continue
    donor.set(parent.id, v)
  }

  const out = donor.size === 0 ? venues : venues.map(v => {
    const from = donor.get(v.id)
    if (!from) return v
    return {
      ...v,
      hero_key: from.hero_key,
      hero_alt: from.hero_alt,
      hero_credit: from.hero_credit,
      hero_license: from.hero_license,
      hero_source_url: from.hero_source_url,
    }
  })

  return { list: out, byId: new Map(out.map(v => [v.id, v])) }
})
